/**
 * IPv4 subnet arithmetic, and the fibre colour order.
 *
 * This is here rather than in a component because it is the substance of the
 * training games, and a quiz that marks a right answer wrong is worse than no
 * quiz — a technician who is told 10.0.0.0/22 has 510 usable hosts will believe
 * it on a pole six months later. So it is pure, and it is tested.
 *
 * All of it is real work at GoSpeedNet: sizing a subnet for a new apartment
 * block, and knowing which strand is which before cutting.
 */
/** `192.168.1.10` → 3232235786. Returns null for anything that is not an address. */
export function ipToInt(ip) {
    const parts = ip.trim().split('.');
    if (parts.length !== 4)
        return null;
    let value = 0;
    for (const part of parts) {
        /**
         * Rejects "1e2", "", " 1" — and "01".
         *
         * The leading zero is the one worth spelling out. `010` is ten to this
         * parser, eight to anything that reads it as octal, and different tools
         * disagree: `ping 010.0.0.1` reaches 8.0.0.1 on many systems. An address
         * whose meaning depends on who is reading it is not an address, so it is
         * refused rather than guessed at.
         */
        if (!/^(0|[1-9]\d{0,2})$/.test(part))
            return null;
        const octet = Number(part);
        if (octet > 255)
            return null;
        value = value * 256 + octet;
    }
    return value;
}
export function intToIp(value) {
    return [24, 16, 8, 0].map((shift) => (value >>> shift) & 255).join('.');
}
/**
 * Describe a block.
 *
 * /31 and /32 are the two that catch people out, and both are real: a /32 is a
 * loopback or a single host route, and a /31 is a point-to-point link where
 * both addresses are usable (RFC 3021) rather than one being the network and
 * one the broadcast. Getting those wrong on a backbone link wastes an afternoon.
 */
export function describeSubnet(ip, prefix) {
    const address = ipToInt(ip);
    if (address === null)
        return null;
    if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32)
        return null;
    // `>>> 0` keeps it unsigned; a /0 mask would otherwise come out as -1.
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    const network = (address & mask) >>> 0;
    const totalAddresses = 2 ** (32 - prefix);
    const broadcast = (network + totalAddresses - 1) >>> 0;
    const usableHosts = prefix === 32 ? 1 : prefix === 31 ? 2 : Math.max(0, totalAddresses - 2);
    const firstHost = prefix >= 31 ? intToIp(network) : totalAddresses > 2 ? intToIp(network + 1) : null;
    const lastHost = prefix === 32
        ? intToIp(network)
        : prefix === 31
            ? intToIp(broadcast)
            : totalAddresses > 2
                ? intToIp(broadcast - 1)
                : null;
    return {
        cidr: `${intToIp(network)}/${prefix}`,
        network: intToIp(network),
        broadcast: intToIp(broadcast),
        mask: intToIp(mask),
        firstHost,
        lastHost,
        usableHosts,
        totalAddresses,
    };
}
/** Do these two addresses sit in the same block? The everyday question. */
export function sameSubnet(a, b, prefix) {
    const left = describeSubnet(a, prefix);
    const right = describeSubnet(b, prefix);
    if (!left || !right)
        return null;
    return left.network === right.network;
}
/** The smallest prefix that holds this many devices, allowing for the two reserved. */
export function prefixForHosts(hosts) {
    if (!Number.isInteger(hosts) || hosts < 1)
        return null;
    for (let prefix = 32; prefix >= 0; prefix -= 1) {
        const usable = prefix === 32 ? 1 : prefix === 31 ? 2 : 2 ** (32 - prefix) - 2;
        if (usable >= hosts)
            return prefix;
    }
    return null;
}
/**
 * TIA-598-C, the order every fibre technician works from.
 *
 * Twelve strands to a tube, twelve tubes to a 144-count cable, and the order is
 * the same at both ends. Knowing it cold is the difference between finding
 * strand 7 in a minute and finding it in twenty.
 */
export const FIBRE_COLOURS = [
    'Blue',
    'Orange',
    'Green',
    'Brown',
    'Slate',
    'White',
    'Red',
    'Black',
    'Yellow',
    'Violet',
    'Rose',
    'Aqua',
];
/** Strand 1 is Blue, not strand 0. Off-by-one here means cutting the wrong fibre. */
export function strandColour(strand) {
    if (!Number.isInteger(strand) || strand < 1 || strand > 12)
        return null;
    return FIBRE_COLOURS[strand - 1];
}
/** Which strand of which tube is this, in a cable of 12-fibre tubes? */
export function locateStrand(absolute) {
    if (!Number.isInteger(absolute) || absolute < 1 || absolute > 144)
        return null;
    const tube = Math.ceil(absolute / 12);
    const strand = ((absolute - 1) % 12) + 1;
    return {
        tube,
        tubeColour: FIBRE_COLOURS[tube - 1],
        strand,
        strandColour: FIBRE_COLOURS[strand - 1],
    };
}
//# sourceMappingURL=netmath.js.map